/**
 * useDocumentMeta — per-route HTML <head> metadata.
 *
 * SPAs share one HTML shell, so `document.title` and `<meta>` tags don't
 * update on route change unless something does it explicitly. This hook
 * sets the title, meta description, canonical URL, Open Graph tags, and
 * Twitter Card on mount, and restores the previous values on unmount so
 * a quick route change doesn't strand stale metadata in the head.
 *
 * Why not react-helmet-async: one usage pattern (set on mount, restore
 * on unmount), 50 lines of code, no dependency, no abandoned-package risk.
 * The library is overkill for a single-purpose need.
 *
 * Crawlers: modern Googlebot executes JS and reads the updated <title>
 * + meta tags, so this works for SEO. For best results we still want
 * pre-rendered HTML on the high-traffic landing pages (Phase 2/3 PRs
 * may add a static-prerender step); this hook is the runtime baseline.
 */

import { useEffect } from 'react';
import { SITE_NAME, SITE_URL, SITE_DESCRIPTION, DEFAULT_OG_IMAGE, TWITTER_HANDLE } from './siteConfig';

export interface DocumentMeta {
  /** Page title — will be suffixed with " · Avium" automatically. Pass null
   *  to use just the site name. */
  title: string | null;
  /** ~150–160 char description for search snippets + social previews. */
  description?: string;
  /** Canonical absolute path (e.g. "/learn/sprint-risk-forecast"). */
  path: string;
  /** Override the default OG image. Absolute URL recommended. */
  ogImage?: string;
  /** Override the meta description for the OG card specifically (most
   *  pages can let it inherit from `description`). */
  ogDescription?: string;
  /** OG type — 'website' is right for most marketing pages; 'article' for
   *  blog posts. Defaults to 'website'. */
  ogType?: 'website' | 'article';
}

// Find-or-create the meta tag with the given selector, set its content,
// remember the prior value so we can restore it on cleanup. The setter
// returns the previous content so the caller can capture it.
function setMeta(selector: string, attrs: Record<string, string>, content: string): string | null {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  const previous = el?.getAttribute('content') ?? null;
  if (!el) {
    el = document.createElement('meta');
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  return previous;
}

function setLink(rel: string, href: string): string | null {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  const previous = el?.getAttribute('href') ?? null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return previous;
}

export function useDocumentMeta(meta: DocumentMeta): void {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = meta.title ? `${meta.title} · ${SITE_NAME}` : SITE_NAME;

    const desc = meta.description ?? SITE_DESCRIPTION;
    const ogDesc = meta.ogDescription ?? desc;
    const ogImage = meta.ogImage ?? DEFAULT_OG_IMAGE;
    const ogType = meta.ogType ?? 'website';
    const canonical = `${SITE_URL}${meta.path}`;

    const prev = {
      description: setMeta('meta[name="description"]', { name: 'description' }, desc),
      canonical:   setLink('canonical', canonical),
      ogTitle:     setMeta('meta[property="og:title"]', { property: 'og:title' }, document.title),
      ogDesc:      setMeta('meta[property="og:description"]', { property: 'og:description' }, ogDesc),
      ogUrl:       setMeta('meta[property="og:url"]', { property: 'og:url' }, canonical),
      ogImage:     setMeta('meta[property="og:image"]', { property: 'og:image' }, ogImage),
      ogType:      setMeta('meta[property="og:type"]', { property: 'og:type' }, ogType),
      ogSite:      setMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, SITE_NAME),
      twCard:      setMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image'),
      twTitle:     setMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, document.title),
      twDesc:      setMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, ogDesc),
      twImage:     setMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, ogImage),
      twSite: TWITTER_HANDLE
        ? setMeta('meta[name="twitter:site"]', { name: 'twitter:site' }, `@${TWITTER_HANDLE}`)
        : null,
    };

    // Restore previous values on unmount so SPA navigation doesn't leak
    // stale metadata. Critical for crawlers that hit a route then follow a
    // link — without restore, the next page inherits the previous page's
    // <title> until its own effect runs.
    return () => {
      document.title = previousTitle;
      if (prev.description !== null) setMeta('meta[name="description"]', { name: 'description' }, prev.description);
      if (prev.canonical   !== null) setLink('canonical', prev.canonical);
      if (prev.ogTitle     !== null) setMeta('meta[property="og:title"]', { property: 'og:title' }, prev.ogTitle);
      if (prev.ogDesc      !== null) setMeta('meta[property="og:description"]', { property: 'og:description' }, prev.ogDesc);
      if (prev.ogUrl       !== null) setMeta('meta[property="og:url"]', { property: 'og:url' }, prev.ogUrl);
      if (prev.ogImage     !== null) setMeta('meta[property="og:image"]', { property: 'og:image' }, prev.ogImage);
      if (prev.ogType      !== null) setMeta('meta[property="og:type"]', { property: 'og:type' }, prev.ogType);
      if (prev.ogSite      !== null) setMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, prev.ogSite);
      if (prev.twCard      !== null) setMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, prev.twCard);
      if (prev.twTitle     !== null) setMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, prev.twTitle);
      if (prev.twDesc      !== null) setMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, prev.twDesc);
      if (prev.twImage     !== null) setMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, prev.twImage);
      if (prev.twSite      !== null) setMeta('meta[name="twitter:site"]', { name: 'twitter:site' }, prev.twSite);
    };
    // We deliberately reset every render that changes any meta field, since
    // some pages compute description dynamically. Stringify the meta object
    // so React shallow-compares the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(meta)]);
}
